import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import SuperSeteGrid from '../../components/lottery/SuperSeteGrid';
import Select from '../../components/ui/Select';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

export default function SuperSetePage() {
  const [selectedNumbers, setSelectedNumbers] = useState<number[][]>([]);
  const [loading, setLoading] = useState(false);
  const [autoMode, setAutoMode] = useState<'por_coluna' | 'por_total'>('por_coluna');
  const [countsPerColumn, setCountsPerColumn] = useState<number[]>(Array(7).fill(1));
  const [totalTarget, setTotalTarget] = useState<number>(7);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSelectionChange = (numbers: number[][]) => {
    setSelectedNumbers(numbers);
  };

  const handleConfirmBet = async () => {
    if (!user) {
      alert('Você precisa estar logado para apostar.');
      return;
    }

    if (selectedNumbers.length !== 7 || selectedNumbers.some(col => col.length === 0)) {
      alert('Por favor, selecione pelo menos 1 número em cada coluna.');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.generateBets({
        lottery_slug: 'super-sete',
        method: 'manual',
        numbers_per_game: getTotalNumbers(),
        games_count: 1,
        manual_numbers: [selectedNumbers],
      });

      navigate('/super-sete/resumo', { 
        state: { 
          numbers: selectedNumbers,
          generationId: response.generation_id,
          ticketId: response.ticket_id 
        } 
      });
    } catch (error) {
      console.error('Erro ao gerar aposta:', error);
      alert('Erro ao gerar aposta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getTotalNumbers = () => {
    return selectedNumbers.reduce((total, col) => total + col.length, 0);
  };

  const isValid = () => {
    return selectedNumbers.length === 7 && selectedNumbers.every(col => col.length >= 1);
  };

  const generateByCounts = (counts: number[]) => {
    const res: number[][] = Array(7).fill(null).map(()=>[] as number[]);
    for (let c = 0; c < 7; c++) {
      const need = Math.max(1, Math.min(3, counts[c] || 1));
      const col: number[] = [];
      let tries = 0;
      while (col.length < need && tries < 200) {
        const n = Math.floor(Math.random()*10);
        if (!col.includes(n)) col.push(n);
        tries++;
      }
      res[c] = col.sort((a,b)=>a-b);
    }
    setSelectedNumbers(res);
  };

  const generateByTotal = (total: number) => {
    const t = Math.max(7, Math.min(21, total || 7));
    const counts = Array(7).fill(1);
    let rem = t - 7;
    let guard = 0;
    while (rem > 0 && guard < 1000) {
      const idx = Math.floor(Math.random()*7);
      if (counts[idx] < 3) { counts[idx]++; rem--; }
      guard++;
    }
    generateByCounts(counts);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <Sidebar />
      
      <div className="ml-72 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Super Sete</h1>
            <p className="text-white/60 text-sm">Escolha 1 número por coluna (0-9) em cada uma das 7 colunas</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column - Game selection */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-bold mb-4">Informações do Jogo</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white/60">Total de números:</span>
                    <span className="font-bold text-cyan-400">{getTotalNumbers()} números</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-white/60">Tipo de aposta:</span>
                    <span className="font-bold">
                      {getTotalNumbers() === 7 ? 'Simples' : 
                       getTotalNumbers() <= 14 ? 'Múltipla (2 números/coluna)' : 
                       'Múltipla (3 números/coluna)'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-white/60">Valor estimado:</span>
                    <span className="font-bold text-green-400">R$ {getTotalNumbers() === 7 ? '2,50' : getTotalNumbers() <= 14 ? '5,00' : '7,50'}</span>
                  </div>
                </div>
              </div>

              {/* Bet confirmation */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold mb-4">Confirmar Aposta</h3>
                
                <div className="space-y-3">
                  <button
                    onClick={handleConfirmBet}
                    disabled={!isValid() || loading}
                    className="w-full px-6 py-4 text-base font-bold bg-gradient-to-r from-green-500 to-green-600 rounded-xl hover:shadow-2xl hover:shadow-green-500/40 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-check-line'} mr-2`}></i>
                    {loading ? 'GERANDO...' : 'CONFIRMAR APOSTA'}
                  </button>
                  
                  <button
                    onClick={() => navigate('/gerar-apostas')}
                    className="w-full px-6 py-3 text-base font-medium bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-go-back-line mr-2"></i>
                    VOLTAR
                  </button>
                </div>
                
                {!isValid() && (
                  <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <div className="text-red-400 text-sm">
                      ⚠️ Selecione pelo menos 1 número em cada coluna para continuar
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right column - Number grid */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-bold mb-4">Gerar automaticamente</h3>
                <div className="space-y-4">
                  <Select
                    value={autoMode}
                    onChange={(v)=>setAutoMode(String(v) as any)}
                    options={[{value:'por_coluna',label:'Por coluna'},{value:'por_total',label:'Por total'}]}
                  />
                  {autoMode === 'por_coluna' ? (
                    <div className="grid grid-cols-7 gap-2">
                      {Array.from({length:7},(_,i)=> (
                        <Select key={i}
                          value={countsPerColumn[i]}
                          onChange={(v)=>{
                            const cp = [...countsPerColumn];
                            cp[i] = Number(v);
                            setCountsPerColumn(cp);
                          }}
                          options={[{value:1,label:'1'},{value:2,label:'2'},{value:3,label:'3'}]}
                          placeholder={`Coluna ${i+1}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <Select
                      value={totalTarget}
                      onChange={(v)=>setTotalTarget(Number(v))}
                      options={Array.from({length:15},(_,k)=>7+k).map(n=>({value:n,label:`${n} números`}))}
                    />
                  )}
                  <div className="flex gap-2">
                    <button onClick={()=> autoMode==='por_coluna' ? generateByCounts(countsPerColumn) : generateByTotal(totalTarget)} className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl text-sm font-bold">GERAR</button>
                    <button onClick={()=> setSelectedNumbers(Array(7).fill(null).map(()=>[]))} className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-sm">LIMPAR</button>
                  </div>
                </div>
              </div>
              <SuperSeteGrid onSelectionChange={handleSelectionChange} selection={selectedNumbers} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
